import type { FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import { PWA_OPAQUE_STORAGE_KEY } from '@/auth/storageKeys';
import { InstallAppPrompt } from '@/components/InstallAppPrompt';

const PRIVACY_POLICY_URL =
  (import.meta.env.VITE_PRIVACY_POLICY_URL as string | undefined)?.trim() ||
  'https://interactivetalker.com/politica-privacidad.html';

export function LoginPage() {
  const { exchange, jwt, bootstrapping } = useSession();
  const nav = useNavigate();
  const [token, setToken] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const autoTried = useRef(false);

  useEffect(() => {
    if (jwt) {
      nav('/', { replace: true });
    }
  }, [jwt, nav]);

  /** Si el token opaco sigue guardado, renovar sesión sin pedir pegarlo otra vez. */
  useEffect(() => {
    if (autoTried.current || bootstrapping || jwt) return;
    const saved = localStorage.getItem(PWA_OPAQUE_STORAGE_KEY);
    if (!saved) return;
    autoTried.current = true;
    setLoading(true);
    setErr(null);
    void (async () => {
      try {
        await exchange(saved);
        nav('/', { replace: true });
      } catch (e) {
        if (e instanceof ApiError) setErr(e.message);
        else setErr('No se pudo restaurar la sesión. Pegá el token de acceso.');
      } finally {
        setLoading(false);
      }
    })();
  }, [bootstrapping, exchange, jwt, nav]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const t = token.trim();
    if (!t) {
      setErr('Pegá el token que te dio la plataforma.');
      return;
    }
    setLoading(true);
    try {
      await exchange(t);
      nav('/', { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        setErr(e.message);
      } else if (e instanceof TypeError) {
        setErr('Sin conexión al servidor. Verificá que el API esté en línea.');
      } else {
        setErr('No se pudo iniciar sesión.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (bootstrapping || (loading && !token.trim())) {
    return (
      <div className="pwa-login-wrap">
        <p className="pwa-muted pwa-boot">Restaurando sesión…</p>
      </div>
    );
  }

  return (
    <div className="pwa-login-wrap">
      <div className="pwa-card" style={{ marginBottom: 0 }}>
        <h1 className="pwa-page-title" style={{ marginBottom: '0.5rem' }}>
          Interactive Talker
        </h1>
        <p className="pwa-muted" style={{ marginTop: 0 }}>
          Pegá el token de acceso que generó un superusuario para tu usuario. Se guarda en este dispositivo para
          renovar la sesión.
        </p>
        <InstallAppPrompt />
        <form onSubmit={onSubmit}>
          <label className="pwa-muted" style={{ fontSize: '0.85rem' }}>
            Token
            <textarea
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="pwa_…"
              rows={4}
              className="pwa-field"
            />
          </label>
          {err ?
            <p className="pwa-err" style={{ marginTop: '0.75rem' }} role="alert">
              {err}
            </p>
          : null}
          <button
            type="submit"
            disabled={loading}
            className="pwa-btn pwa-btn--primary"
            style={{ marginTop: '1rem', width: '100%' }}
          >
            {loading ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
        <p className="pwa-muted" style={{ marginTop: '1rem', fontSize: '0.8rem', textAlign: 'center' }}>
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
            Política de privacidad
          </a>
        </p>
      </div>
    </div>
  );
}
